import { Controller } from "@hotwired/stimulus"
import { apiGet, apiPost, apiPut, apiDelete, formatMoney, t } from "../api_helper"
import { createAccountOffline } from "../lib/offline_api"
import { icon } from "../lib/icons"
import { networkStatus } from "../lib/network_status"
import { isOfflineError, showOfflinePlaceholder, alertOffline } from "../lib/offline_ui"

const ACCOUNT_COLORS = [
  "#3B82F6", "#EF4444", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899",
  "#06B6D4", "#F97316", "#6366F1", "#14B8A6", "#84CC16", "#A855F7"
]

export default class extends Controller {
  static targets = [
    "totalBalance", "accountsList",
    "addModal", "newName", "newIcon", "newCurrency", "newBalance", "newExclude",
    "editModal", "editName", "editBalance", "editExclude",
    "transferModal", "transferFrom", "transferTo", "transferAmount", "transferCurrency", "conversionHint"
  ]

  connect() {
    this.accounts = []
    this.currencies = []
    this.defaultCurrency = "RUB"
    this.rates = {}
    this.selectedNewColor = ACCOUNT_COLORS[0]
    this.editingAccount = null
    this.editingColor = null
    this.load()
  }

  async load() {
    try {
      const data = await apiGet("/api/accounts")
      this.accounts = data.accounts
      this.currencies = data.currencies || []
      this.defaultCurrency = data.default_currency || "RUB"
      this.render(data)
    } catch (e) {
      if (isOfflineError(e)) { showOfflinePlaceholder(this.hasAccountsListTarget ? this.accountsListTarget : null, () => this.load()); return }
      console.error("Accounts load error:", e)
    }
  }

  render(data) {
    if (this.hasTotalBalanceTarget) {
      this.totalBalanceTarget.textContent = formatMoney(data.total_balance, this.defaultCurrency)
      this.totalBalanceTarget.classList.add("amount-animate")
    }

    if (this.hasAccountsListTarget) {
      if (!data.accounts.length) {
        this.accountsListTarget.innerHTML = `
          <div class="text-center py-8">
            <span class="block mb-2">${icon("credit-card", "w-10 h-10 text-slate-300")}</span>
            <div style="color: rgb(148,163,184);">${t("dashboard.no_transactions")}</div>
          </div>`
        return
      }

      this.accountsListTarget.innerHTML = data.accounts.map(a => this.accountCard(a)).join("")
    }

    // Populate currency selectors
    if (this.hasNewCurrencyTarget) {
      this.newCurrencyTarget.innerHTML = this.currencies.map(c =>
        `<option value="${c}" ${c === this.defaultCurrency ? "selected" : ""}>${c}</option>`
      ).join("")
    }
  }

  accountCard(account) {
    const cardBg = account.color ? `color-mix(in srgb, ${account.color} 8%, transparent)` : "rgb(248,250,252)"
    const iconBg = account.color ? `color-mix(in srgb, ${account.color} 18%, transparent)` : "rgb(248,250,252)"
    const defaultBadge = account.is_default
      ? `<span class="text-xs px-2 py-0.5 rounded-full" style="background-color: rgb(99,102,241); color: #ffffff;">${t("accounts.is_default")}</span>`
      : ""

    const currencyBadge = account.currency !== this.defaultCurrency
      ? `<span class="text-xs" style="color: rgb(148,163,184);"> · ${account.currency}</span>`
      : ""

    const excludeBadge = account.exclude_from_total
      ? `<span class="text-xs" style="color: rgb(148,163,184);"> · ${t("accounts.not_in_total")}</span>`
      : ""

    return `
      <div class="rounded-2xl flex items-center gap-3 transition-colors cursor-pointer hover:opacity-90"
           style="padding: 14px 16px; background-color: ${cardBg}; border-left: 3px solid ${account.color || '#3B82F6'};"
           data-action="click->pwa-accounts#showEditModal" data-account-id="${account.id}">
        <div class="flex items-center justify-center flex-shrink-0"
             style="width: 40px; height: 40px; border-radius: 12px; background-color: ${iconBg}; font-size: 20px;">
          ${account.icon}
        </div>
        <div class="flex-1 min-w-0">
          <div class="font-medium text-sm">${account.name}${currencyBadge}${excludeBadge}</div>
          <div class="text-base font-semibold">${formatMoney(account.balance, account.currency)}</div>
        </div>
        <div class="flex flex-col items-end gap-1">
          ${defaultBadge}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgb(148,163,184)" stroke-width="2" stroke-linecap="round"><polyline points="9 18 15 12 9 6"/></svg>
        </div>
      </div>`
  }

  // Color picker HTML helper
  colorPickerHtml(selectedColor, actionName) {
    return ACCOUNT_COLORS.map(c => {
      const ring = c === selectedColor ? `box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px ${c};` : ""
      return `<button type="button" data-action="click->pwa-accounts#${actionName}" data-color="${c}"
                      class="transition-shadow" style="width: 32px; height: 32px; border-radius: 50%; background-color: ${c}; ${ring} border: none; cursor: pointer;"></button>`
    }).join("")
  }

  // Add Account Modal
  showAddModal() {
    this.selectedNewColor = ACCOUNT_COLORS[0]
    if (this.hasAddModalTarget) {
      this.addModalTarget.classList.remove("hidden")
      this.renderNewColorPicker()
      if (this.hasNewNameTarget) this.newNameTarget.value = ""
      if (this.hasNewIconTarget) this.newIconTarget.value = ""
      if (this.hasNewBalanceTarget) this.newBalanceTarget.value = ""
      if (this.hasNewExcludeTarget) this.newExcludeTarget.checked = false
    }
  }

  hideAddModal() {
    if (this.hasAddModalTarget) this.addModalTarget.classList.add("hidden")
  }

  renderNewColorPicker() {
    const el = this.addModalTarget.querySelector("[data-color-picker]")
    if (el) el.innerHTML = this.colorPickerHtml(this.selectedNewColor, "selectNewColor")
  }

  selectNewColor(event) {
    this.selectedNewColor = event.currentTarget.dataset.color
    this.renderNewColorPicker()
  }

  async createAccount() {
    const name = this.newNameTarget.value.trim()
    if (!name) return

    const currency = this.hasNewCurrencyTarget ? this.newCurrencyTarget.value : this.defaultCurrency
    const initialBalance = this.hasNewBalanceTarget ? parseFloat(this.newBalanceTarget.value) || 0 : 0

    try {
      await createAccountOffline({
        name: name,
        icon: this.newIconTarget.value.trim() || "card",
        color: this.selectedNewColor,
        currency: currency,
        initial_balance: initialBalance,
        exclude_from_total: this.hasNewExcludeTarget ? this.newExcludeTarget.checked : false
      })
      this.hideAddModal()
      this.newNameTarget.value = ""
      this.newIconTarget.value = ""
      if (this.hasNewBalanceTarget) this.newBalanceTarget.value = ""
      this.load()
    } catch (e) {
      alert(e.message || t("add_transaction.save_error"))
    }
  }

  // Edit Account Modal
  showEditModal(event) {
    const accountId = parseInt(event.currentTarget.dataset.accountId)
    this.editingAccount = this.accounts.find(a => a.id === accountId)
    if (!this.editingAccount || !this.hasEditModalTarget) return

    this.editingColor = this.editingAccount.color || ACCOUNT_COLORS[0]
    this.editNameTarget.value = this.editingAccount.name
    this.editBalanceTarget.value = this.editingAccount.balance
    if (this.hasEditExcludeTarget) this.editExcludeTarget.checked = !!this.editingAccount.exclude_from_total

    this.renderEditColorPicker()
    this.renderEditActions()
    this.editModalTarget.classList.remove("hidden")
  }

  hideEditModal() {
    if (this.hasEditModalTarget) this.editModalTarget.classList.add("hidden")
    this.editingAccount = null
  }

  renderEditColorPicker() {
    const el = this.editModalTarget.querySelector("[data-color-picker]")
    if (el) el.innerHTML = this.colorPickerHtml(this.editingColor, "selectEditColor")
  }

  renderEditActions() {
    const el = this.editModalTarget.querySelector("[data-edit-actions]")
    if (!el || !this.editingAccount) return

    const defaultBtn = this.editingAccount.is_default
      ? ""
      : `<button data-action="click->pwa-accounts#setDefaultFromEdit"
                 class="w-full py-2.5 rounded-2xl text-sm font-medium transition-colors hover:opacity-90"
                 style="background-color: rgb(248,250,252); color: inherit;">
           ${t("accounts.set_as_default")}
         </button>`

    const deleteBtn = this.editingAccount.is_default
      ? ""
      : `<button data-action="click->pwa-accounts#deleteAccountFromEdit"
                 class="w-full py-2.5 rounded-2xl text-sm font-medium transition-colors hover:opacity-90"
                 style="background-color: rgba(239, 68, 68, 0.1); color: rgb(239,68,68);">
           ${t("dashboard.delete")}
         </button>`

    el.innerHTML = defaultBtn + deleteBtn
  }

  selectEditColor(event) {
    this.editingColor = event.currentTarget.dataset.color
    this.renderEditColorPicker()
  }

  async saveEdit() {
    if (!networkStatus.online) { alertOffline(); return }
    if (!this.editingAccount) return

    const name = this.editNameTarget.value.trim()
    if (!name) return

    const newBalance = parseFloat(this.editBalanceTarget.value)
    if (isNaN(newBalance)) return

    const balanceChanged = Math.abs(newBalance - this.editingAccount.balance) >= 0.01

    try {
      const payload = { account: { name, color: this.editingColor } }
      if (this.hasEditExcludeTarget) {
        payload.account.exclude_from_total = this.editExcludeTarget.checked
      }
      if (balanceChanged) {
        payload.target_balance = newBalance
      }

      await apiPut(`/api/accounts/${this.editingAccount.id}`, payload)
      this.hideEditModal()
      this.load()
    } catch (e) {
      alert(e.message || t("add_transaction.save_error"))
    }
  }

  async setDefaultFromEdit() {
    if (!networkStatus.online) { alertOffline(); return }
    if (!this.editingAccount) return
    try {
      await apiPut(`/api/accounts/${this.editingAccount.id}/set_default`)
      this.hideEditModal()
      this.load()
    } catch (e) {
      console.error("Set default error:", e)
    }
  }

  async deleteAccountFromEdit() {
    if (!networkStatus.online) { alertOffline(); return }
    if (!this.editingAccount) return
    if (!confirm(t("accounts.delete_confirm"))) return

    try {
      await apiDelete(`/api/accounts/${this.editingAccount.id}`)
      this.hideEditModal()
      this.load()
    } catch (e) {
      alert(e.message || t("add_transaction.save_error"))
    }
  }

  // Transfer Modal
  showTransferModal() {
    if (this.accounts.length < 2) {
      alert(t("accounts.need_two_accounts") || "Need at least 2 accounts")
      return
    }

    const options = this.accounts.map(a =>
      `<option value="${a.id}" data-currency="${a.currency}">${a.icon} ${a.name} (${a.currency})</option>`
    ).join("")
    this.transferFromTarget.innerHTML = options
    this.transferToTarget.innerHTML = options

    if (this.accounts.length > 1) {
      this.transferToTarget.selectedIndex = 1
    }

    // Populate currency selector with unique currencies from accounts
    if (this.hasTransferCurrencyTarget) {
      const accCurrencies = [...new Set(this.accounts.map(a => a.currency))]
      this.transferCurrencyTarget.innerHTML = accCurrencies.map(c =>
        `<option value="${c}">${c}</option>`
      ).join("")
    }

    if (this.hasConversionHintTarget) this.conversionHintTarget.textContent = ""
    if (this.hasTransferAmountTarget) this.transferAmountTarget.value = ""
    if (this.hasTransferModalTarget) this.transferModalTarget.classList.remove("hidden")
  }

  hideTransferModal() {
    if (this.hasTransferModalTarget) this.transferModalTarget.classList.add("hidden")
  }

  updateConversion() {
    if (!this.hasConversionHintTarget) return

    const amount = parseFloat(this.transferAmountTarget.value)
    if (!amount || amount <= 0) {
      this.conversionHintTarget.textContent = ""
      return
    }

    const inputCurrency = this.hasTransferCurrencyTarget ? this.transferCurrencyTarget.value : this.defaultCurrency
    const fromOpt = this.transferFromTarget.selectedOptions[0]
    const toOpt = this.transferToTarget.selectedOptions[0]
    const fromCurrency = fromOpt?.dataset?.currency
    const toCurrency = toOpt?.dataset?.currency

    if (!fromCurrency || !toCurrency) return
    if (fromCurrency === toCurrency && inputCurrency === fromCurrency) {
      this.conversionHintTarget.textContent = ""
      return
    }

    let parts = []
    if (inputCurrency !== fromCurrency) {
      parts.push(`-${formatMoney(amount, inputCurrency)} → ${fromCurrency}`)
    }
    if (inputCurrency !== toCurrency) {
      parts.push(`+${formatMoney(amount, inputCurrency)} → ${toCurrency}`)
    }
    this.conversionHintTarget.textContent = parts.length ? parts.join(" | ") : ""
  }

  async submitTransfer() {
    if (!networkStatus.online) { alertOffline(); return }
    const fromId = this.transferFromTarget.value
    const toId = this.transferToTarget.value
    const amount = parseFloat(this.transferAmountTarget.value)
    const inputCurrency = this.hasTransferCurrencyTarget ? this.transferCurrencyTarget.value : null

    if (!amount || amount <= 0) return
    if (fromId === toId) return

    try {
      await apiPost("/api/accounts/transfer", {
        from_account_id: fromId,
        to_account_id: toId,
        amount: amount,
        input_currency: inputCurrency
      })
      this.hideTransferModal()
      this.transferAmountTarget.value = ""
      this.load()
    } catch (e) {
      alert(e.message || t("add_transaction.save_error"))
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }
}
